import {Row, Col, Form, Input, Button, Select, Grid} from "antd";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ApiService from "../../../core/service/ApiService";
import Helper from "../../../core/Helper";
import FirmSelectList from "../../firm/FirmSelectList";
import UserSelectList from "../../user/UserSelectList";
import _ from "lodash";
import AppointmentTypeSelect from "../AppointmentTypeSelect";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { FieldHolder, MoreFilters, QuickDateRange, QuickSearchBox } from "../../../core/components/quick-filter/QuickFilterParts";
import { matchesSearch } from "../../../core/utils/text-search";

// Sık kullanılan filtre satırının alanları — mobilde katlıyken düğmedeki sayaç bunlara bakar.
const QUICK_FILTER_FIELDS = ['unitIdList', 'institutionIdList', 'appointmentTypeIdList', 'paymentMethodIdList', 'stateIdList', 'responsibleUserId', 'entitlementConfirmStatusList', 'entitlementConfirmUserId', 'entitlementCompleted'];

// Hakediş sekmesindeki tarih türü (sunucu: AppointmentFilter.DateFilterType).
const DATE_FILTER_TYPES = [
    { value: 'appointmentDate', label: 'Appointment' },
    { value: 'entitlementConfirmDate', label: 'Entitlement Confirm' },
];

export default function AppointmentFilter({ view }) {
    const { queryModel, onFilterPageSort } = useDataGrid();
    const [form] = Form.useForm();
    const formRef = useRef();
    const methodsRef = useRef([])

    const [doctors, setDoctors] = useState([])
    const [institutions, setInstitutions] = useState([]);
    const [units, setUnits] = useState([])
    const [states, setStates] = useState([]);
    const [stateGroups, setStateGroups] = useState([]);
    const { firmSettings, hasPermission } = useUser();
    const [methods, setMethods] = useState([])
    // Hızlı Arama doluyken sunucu tarih filtresini uygulamıyor → tarih bloğu kilitlenir.
    const searchText = Form.useWatch('searchText', form) || ''
    const dateFilterType = Form.useWatch('dateFilterType', form) || 'appointmentDate'
    const searchActive = !!searchText.trim()
    // Mobilde filtre satırı varsayılan katlı; alanlar gizli kalsa da formda kayıtlı (yalnız CSS ile saklanır).
    const screens = Grid.useBreakpoint()
    const [quickOpen, setQuickOpen] = useState(false)
    const quickActiveCount = QUICK_FILTER_FIELDS.filter(name => {
        const v = queryModel.filter?.[name]
        if (Array.isArray(v)) return v.length > 0
        return v !== undefined && v !== null && `${v}`.trim() !== ''
    }).length + (view === 'entitlement' && (queryModel.filter?.dateFilterType || 'appointmentDate') !== 'appointmentDate' ? 1 : 0)
    const quickCollapsed = !screens.md && !quickOpen

    useEffect(() => {
        formRef.current.setFieldsValue(queryModel.filter)
        Helper.UrlParams.updateUrlParam(queryModel.filter)
    }, [queryModel.updateKey])

    const load = async () => {
        return Promise.all([
            ApiService.Institution.GetInstitutionList(true).then(res => {
                if (res.success) setInstitutions(res.data)
            }),
            ApiService.Doctor.GetDoctorList({}).then(res => {
                if (res.success) setDoctors(res.data)
            }),
            ApiService.Unit.GetUnitList().then(res => {
                if (res.success) setUnits(res.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` })))
            }),
            ApiService.State.GetStateList().then(res => {
                if (res.success) {
                    setStates(res.data)
                    let groups = []
                    for (const item of res.data) {
                        const group = item.stateGroup
                        const exist = groups.find(x => x.id === group.id)
                        if (!exist) groups.push(group)
                    }
                    setStateGroups(groups)
                }
            }),
            ApiService.PaymentMethod.GetPaymentMethodList().then(res => {
                if (res.success) {
                    methodsRef.current = res.data
                    setMethods(res.data)
                }
            })
        ])
    }

    useEffect(() => { load() }, [])

    const onFinish = async (formData) => {
        let _states = []
        for (const st of (formData?.stateIdList || [])) {
            const group = stateGroups.find(x => x.name === st)
            if (group) {
                const values = states.filter(x => x.stateGroup?.name === st).map(x => x.id)
                _states = [..._states, ...values]
            } else {
                _states.push(st)
            }
        }
        onFilterPageSort({
            filter: { ...formData, stateIdList: _states }
        })
    }

    // "Diğer filtreler" paneli. Sayaç uygulanan filtreye bakar; defaultValue'daki alan etkin sayılmaz.
    const searchFilterOption = (input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))
    const moreFilters = [
        { name: 'firmIdList', label: 'Firm', render: () => <FirmSelectList size="small" style={{ width: '100%' }} allowClear mode={'multiple'} maxTagCount={'responsive'} /> },
        { name: 'doctorId', label: 'Doctor', render: () => <Select size="small" allowClear showSearch style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={doctors} filterOption={searchFilterOption} /> },
        { name: 'patientParentName', label: 'Mother / Father Name', render: () => <Input size="small" allowClear style={{ width: '100%' }} /> },
        { name: 'logsUserId', label: 'Logs User', visible: hasPermission(PERMISSIONS.FILTER_LOGS_BY_USER), render: () => <UserSelectList size="small" allowClear style={{ width: '100%' }} /> },
        { name: 'provisionNo', label: 'Provision No', visible: view === 'accounting', render: () => <Input size="small" allowClear style={{ width: '100%' }} /> },
        {
            name: 'paymentStatus', label: 'Payment Status', visible: view === 'accounting', defaultValue: 2,
            render: () => <Select size="small" style={{ width: '100%' }}><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={0}>{t('Balance')}</Select.Option><Select.Option value={1}>{t('Waiting')}</Select.Option><Select.Option value={2}>{t('Done')}</Select.Option><Select.Option value={3}>{t('Reject')}</Select.Option></Select>
        },
    ]

    const getMethods = () => {
        const group = _.groupBy(methods, (x) => x?.firm?.name)
        return Object.keys(group).map(key => (
            <Select.OptGroup key={key} label={key}>
                {group[key].map(x => (<Select.Option key={x.id} value={x.id}>{x.name}</Select.Option>))}
            </Select.OptGroup>
        ))
    }

    return (
        <>
            <Form
                form={form}
                layout={'vertical'}
                ref={formRef}
                onFinish={onFinish}
                size={'small'}
                initialValues={{
                    isDeleted: false, dateFilterType: 'appointmentDate', paymentStatus: 2, hasBalance: false
                }}
            >
                {/* --- TARİH + ARAMA (RIS hızlı filtre çubuğu düzeni) --- */}
                <Form.Item name={'startDate'} noStyle><FieldHolder /></Form.Item>
                <Form.Item name={'endDate'} noStyle><FieldHolder /></Form.Item>
                <Form.Item name={'dateFilterType'} noStyle><FieldHolder /></Form.Item>
                {/* Grid araç çubuğundaki anahtarlar (AppointmentGridToggles) — "Ara" bunları da göndersin. */}
                <Form.Item name={'hasBalance'} noStyle><FieldHolder /></Form.Item>
                <Form.Item name={'isDeleted'} noStyle><FieldHolder /></Form.Item>
                <div className="qf-row qf-row-top">
                    <QuickDateRange form={form} disabled={searchActive} label={t('Date Filter Type')} separate>
                        {/* Hakediş sekmesi: tarihlerin hangi alana uygulanacağı, aralığın solunda.
                            Mobilde burada değil: en üstte sekme gibi dikkat çekiyordu → Filtreler bölümünde seçim kutusu. */}
                        {view === 'entitlement' && screens.md && (
                            <div className="qf-seg">
                                {DATE_FILTER_TYPES.map(type => (
                                    <button
                                        key={type.value}
                                        type="button"
                                        disabled={searchActive}
                                        aria-pressed={dateFilterType === type.value}
                                        onClick={() => {
                                            if (dateFilterType === type.value) return
                                            form.setFieldsValue({ dateFilterType: type.value })
                                            form.submit()
                                        }}
                                    >
                                        {t(type.label)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </QuickDateRange>

                    {/* Mobil: [arama] [Filtreler] [Ara] tek satır; Excel grid araç çubuğundaki İşlemler menüsünde */}
                    <div className="appt-filter__search-row">
                        <QuickSearchBox
                            form={form}
                            hint={t('Search by - Patient ID, Name, Phone, Email - Patient Note - Appointment Note, Anamnez, - Accession No etc.')}
                        />
                        {!screens.md && (
                            <>
                                <button
                                    type="button"
                                    className="qf-group appt-filter__toggle"
                                    aria-label={t('Filters')}
                                    aria-expanded={!quickCollapsed}
                                    aria-pressed={quickActiveCount > 0}
                                    onClick={() => setQuickOpen(o => !o)}
                                >
                                    <FilterOutlined aria-hidden="true" />
                                    {quickActiveCount > 0 && <span className="qf-count">{quickActiveCount}</span>}
                                </button>
                                <Button type={'primary'} htmlType={'submit'} className="appt-filter__mobile-action">
                                    {t('Search')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* --- SIK KULLANILAN FİLTRELER: etiketsiz, tek satır (placeholder = alan adı) --- */}
                <div className={`qf-row appt-filter__quick${quickCollapsed ? ' is-collapsed' : ''}`}>
                    {view === 'entitlement' && !screens.md && (
                        <div className="qf-group appt-filter__field">
                            <Select
                                size="small"
                                style={{ width: '100%' }}
                                value={dateFilterType}
                                disabled={searchActive}
                                options={DATE_FILTER_TYPES.map(type => ({ value: type.value, label: `${t('Date')}: ${t(type.label)}` }))}
                                onChange={(value) => {
                                    form.setFieldsValue({ dateFilterType: value })
                                    form.submit()
                                }}
                            />
                        </div>
                    )}
                    <div className="qf-group appt-filter__field">
                        <Form.Item name={'unitIdList'} noStyle>
                            <Select size="small" placeholder={t('Unit')} allowClear style={{ width: '100%' }} showSearch mode={'tags'} maxTagCount={'responsive'} fieldNames={{ label: 'name', value: 'id' }} options={units} filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} />
                        </Form.Item>
                    </div>
                    <div className="qf-group appt-filter__field">
                        <Form.Item name={'institutionIdList'} noStyle>
                            <Select size="small" placeholder={t('Institution')} allowClear showSearch mode={'multiple'} maxTagCount={'responsive'} style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={institutions} filterOption={(input, option) => Helper.Text.normalizeText(JSON.stringify(option)).includes(Helper.Text.normalizeText(input))} />
                        </Form.Item>
                    </div>
                    <div className="qf-group appt-filter__field">
                        <Form.Item name={'appointmentTypeIdList'} noStyle>
                            <AppointmentTypeSelect placeholder={t('Appointment Type')} style={{ width: '100%' }} mode={'multiple'} maxTagCount={'responsive'} allowClear />
                        </Form.Item>
                    </div>
                    {view !== 'entitlement' && (
                        <div className="qf-group appt-filter__field">
                            <Form.Item name={'paymentMethodIdList'} noStyle>
                                <Select size="small" placeholder={t('Payment Method')} mode={'multiple'} maxTagCount={'responsive'} allowClear showSearch style={{ width: '100%' }}>{getMethods()}</Select>
                            </Form.Item>
                        </div>
                    )}
                    <div className="qf-group appt-filter__field">
                        <Form.Item name={'stateIdList'} noStyle>
                            <Select
                                size="small"
                                mode={'tags'}
                                maxTagCount={'responsive'}
                                placeholder={t('State')}
                                tagRender={(props) => {
                                    const { value, label, onClose } = props;
                                    const state = states.find(x => x.id === value);
                                    const group = stateGroups.find(x => x.name === value);

                                    // Fallback if not found (e.g. initial load or custom value)
                                    if (!state && !group) return <span className="ant-select-selection-item-content">{label}</span>;

                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 4, padding: '0 4px', border: '1px solid #d9d9d9', borderRadius: 2, background: '#fafafa' }}>
                                            <div style={{ width: 10, height: 10, backgroundColor: state?.color || group?.color || '#ccc', border: '1px solid #000' }} />
                                            <span style={{ marginRight: 4 }}>{label}</span>
                                            <span onClick={onClose} style={{ cursor: 'pointer' }}>x</span>
                                        </div>
                                    );
                                }}
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                filterOption={(input, option) => matchesSearch(JSON.stringify(option), input)}
                            >
                                <Select.OptGroup label={t('Group')}>
                                    {stateGroups.map(x => (
                                        <Select.Option key={x.name} value={x.name}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                {t(x.name)}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                                <Select.OptGroup value={'X'} label={t('Before Check-in')}>
                                    {states.filter(x => !x.checkInRequired).map(x => (
                                        <Select.Option key={x.id} value={x.id}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                {x.name}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                                <Select.OptGroup value={'Y'} label={t('After Check-in')}>
                                    {states.filter(x => x.checkInRequired).map(x => (
                                        <Select.Option key={x.id} value={x.id}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 12, height: 12, backgroundColor: x.color, border: '1px solid #000' }} />
                                                {x.name}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            </Select>
                        </Form.Item>
                    </div>
                    {hasPermission(PERMISSIONS.ENTITLEMENT_RESPONSIBLE_FILTER) && (
                        <div className="qf-group appt-filter__field">
                            <Form.Item name={'responsibleUserId'} noStyle>
                                <UserSelectList placeholder={t('Responsible')} allowClear style={{ width: '100%' }} />
                            </Form.Item>
                        </div>
                    )}
                    {/* Hakediş sekmesi: üç hakediş filtresi tek kartta, "Diğer filtreler"in hemen solunda. */}
                    {view === 'entitlement' && (
                        <div className="qf-group appt-filter__field appt-filter__field--group" role="group" aria-label={t('Entitlement')}>
                            <Form.Item name={'entitlementConfirmStatusList'} noStyle>
                                <Select size="small" placeholder={t('Entitlement Confirmation Status')} mode={'multiple'} maxTagCount={'responsive'} allowClear><Select.Option value={null}>{t('All')}</Select.Option><Select.Option value={0}>{t('No Action')}</Select.Option><Select.Option value={1}>{t('Waiting')}</Select.Option><Select.Option value={2}>{t('Confirmed')}</Select.Option><Select.Option value={3}>{t('Canceled')}</Select.Option></Select>
                            </Form.Item>
                            <Form.Item name={'entitlementConfirmUserId'} noStyle>
                                <UserSelectList size="small" placeholder={t('Entitlement Confirm User')} showEntitlementConfirmUser={true} allowClear style={{}} />
                            </Form.Item>
                            <Form.Item name={'entitlementCompleted'} noStyle>
                                <Select size="small" placeholder={t('Entitlement')} allowClear><Select.Option value={true}>{t('Completed')}</Select.Option><Select.Option value={false}>{t('Not Completed')}</Select.Option></Select>
                            </Form.Item>
                        </div>
                    )}
                    <MoreFilters form={form} fields={moreFilters} appliedFilter={queryModel.filter} />
                </div>

                {/* --- MAIN FILTERS (Always Visible) — mobilde Ara arama kutusunun yanında --- */}
                {screens.md && <Row gutter={[12, 0]}>
                    {/* Butonlar */}
                    <Col xs={24} sm={24} md={8} lg={6} xl={4} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 8 }}>
                        <Button type={'primary'} htmlType={'submit'} style={{ flex: 1, maxWidth: 120 }} icon={<SearchOutlined />}>
                            {t('Search')}
                        </Button>
                    </Col>
                </Row>}

            </Form>
        </>
    )
}